import {
  addMspTreasuryCredits,
  toggleMspSubscription,
  transferMspCredits,
  updateSubscriptionStatus,
} from "stores/slice/bogus/license/const";
import {
  LICENSE_SERVICE_ACTION_ACTIVE,
  UNITY_PRO_ACCESS_POINT_DAILY_CREDITS,
  UNITY_PRO_SERVICE_KEY,
  UNITY_PRO_SWITCH_DAILY_CREDITS,
} from "const/license";

const getOrgServiceStatus = ({ state, restfulPlaceholders }) => {
  const serviceStatus = state.license.serviceStatus;

  if (!restfulPlaceholders?.entId) return serviceStatus;

  return {
    ...serviceStatus,
    data: {
      ...serviceStatus.data,
      org: {
        ...serviceStatus.data.org,
        underMsp: true,
      },
    },
  };
};

const getServiceSubscribePreConfirm = ({ state, params }) => {
  const request = params?.data || {};
  const site = state.license.serviceStatus.data.org.sites.find(
    (item) => item.id === request.siteId,
  );
  const accessPoints = Number(site?.accessPoints) || 0;
  const switches = Number(site?.switches) || 0;
  const deviceCount = accessPoints + switches;
  const dailyRate =
    accessPoints * UNITY_PRO_ACCESS_POINT_DAILY_CREDITS +
    switches * UNITY_PRO_SWITCH_DAILY_CREDITS;
  const today = new Date();
  const remainingDays = Math.max(
    1,
    new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate() -
    today.getDate() +
    1,
  );
  const isActivation = request.action === LICENSE_SERVICE_ACTION_ACTIVE;
  const walletBalance = Number(state.license.serviceStatus.data.org.walletBalance) || 0;

  return {
    data: [
      {
        siteId: request.siteId,
        siteName: site?.name || "Unknown Site",
        service: [UNITY_PRO_SERVICE_KEY],
        action: isActivation ? "add" : "remove",
        deviceCount,
        deviceTotalCount: deviceCount,
        rate: `${UNITY_PRO_ACCESS_POINT_DAILY_CREDITS} Credits/device/day`,
        dailyRate,
        remainingDays,
        sufficientCredits: !isActivation || walletBalance >= dailyRate * remainingDays,
      },
    ],
  };
};

const getOrgHistoryExport = ({ state, params }) => {
  const month = params?.filter?.month;
  const history = state.license.orgHistory.data || [];

  return {
    data: month
      ? history.filter((transaction) => (
        new Date(transaction.timestamp * 1000).toISOString().slice(0, 7) === month
      ))
      : history,
  };
};

const licenseConfig = {
  "/ents/:entId/license/service/status.read": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/ents/:entId/license/service/status",
    mockHandler: ({ state, restfulPlaceholders }) => (
      state.license.mspServiceStatus[restfulPlaceholders?.entId]
      || state.license.mspServiceStatus.default
    ),
  },
  "/ents/:entId/license/dashboard.read": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/ents/:entId/license/dashboard",
    mockHandler: ({ state, restfulPlaceholders }) => (
      state.license.mspDashboard[restfulPlaceholders?.entId]
      || state.license.mspDashboard.default
    ),
  },
  "/ents/:entId/license/history.read": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/ents/:entId/license/history",
    mockSelector: "license.mspHistory",
  },
  "/ents/:entId/license/add.create": {
    protocol: "ws",
    method: "create",
    restful: true,
    resource: "/ents/:entId/license/add",
    action: addMspTreasuryCredits,
    mockSelector: "license.mspImport",
  },
  // MSP License Management: move credits between the MSP treasury and an org.
  // This is deliberately separate from the Org-level license data sources.
  "/ents/:entId/orgs/:orgId/license/allocate.update": {
    protocol: "ws",
    method: "update",
    restful: true,
    resource: "/ents/:entId/orgs/:orgId/license/allocate",
    buildParams: ({ action, amount }) => ({ action, amount }),
    action: transferMspCredits,
    mockSelector: "license.mspTransfer",
  },
  "/ents/:entId/orgs/:orgId/license/history/export.read": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/ents/:entId/orgs/:orgId/license/history/export",
    mockHandler: getOrgHistoryExport,
  },
  "/ents/:entId/license/subscriptions.update": {
    protocol: "ws",
    method: "update",
    restful: true,
    resource: "/ents/:entId/license/subscriptions",
    action: toggleMspSubscription,
    mockSelector: "license.mspSubscriptionToggle",
  },
  "/ents/:entId/orgs/license/summary.read": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/ents/:entId/orgs/license/summary",
    mockSelector: "license.mspOrgLicenseSummary",
  },

  // Dashboard
  "/orgs/:orgId/license/dashboard.read": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/orgs/:orgId/license/dashboard",
    mockSelector: "license.dashboard",
  },
  "/ents/:entId/orgs/:orgId/license/dashboard.read": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/ents/:entId/orgs/:orgId/license/dashboard",
    mockSelector: "license.dashboard",
  },

  // Service Status
  // Site responses expose `data.services.unityPro` as the sole Site plan
  // entitlement. The route and request method remain unchanged.
  "/orgs/:orgId/license/service/status.read": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/orgs/:orgId/license/service/status",
    mockHandler: getOrgServiceStatus,
  },
  "/ents/:entId/orgs/:orgId/license/service/status.read": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/ents/:entId/orgs/:orgId/license/service/status",
    mockHandler: getOrgServiceStatus,
  },
  "/orgs/:orgId/sites/:siteId/license/service/status.read": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/orgs/:orgId/sites/:siteId/license/service/status",
    mockSelector: "license.siteServiceStatus",
  },
  "/ents/:entId/orgs/:orgId/sites/:siteId/license/service/status.read": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/ents/:entId/orgs/:orgId/sites/:siteId/license/service/status",
    mockSelector: "license.siteServiceStatus",
  },

  // History
  "/orgs/:orgId/license/history.read": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/orgs/:orgId/license/history",
    mockSelector: "license.orgHistory",
  },
  "/ents/:entId/orgs/:orgId/license/history.read": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/ents/:entId/orgs/:orgId/license/history",
    mockSelector: "license.orgHistory",
  },

  // Import
  "/orgs/:orgId/license/add.create": {
    protocol: "ws",
    method: "create",
    restful: true,
    resource: "/orgs/:orgId/license/add",
    mockSelector: "license.import",
  },
  "/ents/:entId/orgs/:orgId/license/add.create": {
    protocol: "ws",
    method: "create",
    restful: true,
    resource: "/ents/:entId/orgs/:orgId/license/add",
    mockSelector: "license.import",
  },

  // Subscriptions
  "/orgs/:orgId/license/subscriptions.read": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/orgs/:orgId/license/service/status",
    mockHandler: getOrgServiceStatus,
  },
  "/ents/:entId/orgs/:orgId/license/subscriptions.read": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/ents/:entId/orgs/:orgId/license/service/status",
    mockHandler: getOrgServiceStatus,
  },
  "/orgs/:orgId/license/subscriptions.update": {
    protocol: "ws",
    method: "update",
    restful: true,
    resource: "/orgs/:orgId/license/service/subscribe",
    action: updateSubscriptionStatus,
    mockSelector: "license.subscribeResult",
  },
  "/ents/:entId/orgs/:orgId/license/subscriptions.update": {
    protocol: "ws",
    method: "update",
    restful: true,
    resource: "/ents/:entId/orgs/:orgId/license/service/subscribe",
    action: updateSubscriptionStatus,
    mockSelector: "license.subscribeResult",
  },

  // Service PreConfirm
  "/orgs/:orgId/license/service/subscribe/preConfirm": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/orgs/:orgId/license/service/subscribe/preConfirm",
    mockHandler: getServiceSubscribePreConfirm,
  },
  "/ents/:entId/orgs/:orgId/license/service/subscribe/preConfirm": {
    protocol: "ws",
    method: "read",
    restful: true,
    resource: "/ents/:entId/orgs/:orgId/license/service/subscribe/preConfirm",
    mockHandler: getServiceSubscribePreConfirm,
  },
};

export default licenseConfig;
